/* Core & libraries */
import React, { useEffect, useState } from 'react';
import { useTheme } from 'react-jss';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

/* Helpers, constants, custom methods, internal libraries */
import { useGuestProfileService } from "../../../services/guestProfileService";
import { useHasPermission } from 'hooks/useHasPermission';

/* Components */
import GridFilters from '../../ucgrid/filters';
import GuestGrid from './guestGrid';
import UCButton from 'components/buttons';
import { ReactComponent as DownloadIcon } from "../../../resources/images/download-02.svg";

/* Styles */
import styles from './styles.js';


const GuestTab = ({getGuestProfilesAction, guestProfiles}) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const location = useLocation();
  const navigate = useNavigate();
  const { searchProfile, deleteGuestProfile, downloadGuestUsersGridData } = useGuestProfileService();
  const { checkPermission } = useHasPermission();
  const noGuestProfilesData = guestProfiles?.results?.resultTotal <= 0
  const [gridOptions, setGridOptions] = useState(() => {
    const initialPage = location.pathname.includes('/guest')
      ? new URLSearchParams(location.search).get('page') || 1
      : 1;
    return {
      page: +initialPage,
      filters: {
        LABEL: [],
      },
    };
  });

  useEffect(() => {
    (async () => {
      await getGuestProfilesAction(gridOptions);
    })();
    gridOptions.page > 1 ? navigate(`/app/guest?page=${gridOptions.page}`) : navigate(`/app/guest`)
  }, [gridOptions]);

  useEffect(() => {
    const pageFromURL = location.pathname.includes('/guest')
      ? new URLSearchParams(location.search).get('page') || 1
      : 1;
    if (+pageFromURL !== gridOptions.page) {
      setGridOptions({ ...gridOptions, page: pageFromURL });
    }
  }, [location.search]);

  const handleDeleteGuestProfile = async (entityId) => {
    const lastGuestOnPage = guestProfiles?.results?.dataItems?.length === 1;
    
    const response = await deleteGuestProfile(entityId);
    if (!response) return false;
    
    const nextPage =
      lastGuestOnPage && gridOptions.page > 1
        ? gridOptions.page - 1
        : gridOptions.page;

    setGridOptions(prev => ({
      ...prev,
      page: nextPage
    }));

    return true;
  };

  const handleDownloadGuestProfilesGrid = () => {
    downloadGuestUsersGridData()
  }


  return (
    <div>
      <div className={classes.filtersContainer}>
        <GridFilters
          response={guestProfiles}
          gridOptions={gridOptions}
          setGridOptions={setGridOptions}
          search={term => searchProfile(term)}
          type='GUEST'
          disabled={Object.keys(guestProfiles).length === 0 || guestProfiles.results === null}
          hide={Object.keys(guestProfiles).length === 0 || guestProfiles.results === null}
          className={classes.autocomplete}
        />
        {checkPermission('F_GUESTS_REPORT_DOWNLOAD') && (
          <UCButton
            onClick={handleDownloadGuestProfilesGrid}
            type="default"
            text={<FormattedMessage id="people.grids.download" />}
            className={`${classes.downloadUsersBtn} ${noGuestProfilesData ? `${classes.downloadUsersDisabledBtn}` : ``}`}
            icon={<DownloadIcon/>}
            loadingStateStyling={classes.downloadLoadingState}
            disabled={noGuestProfilesData}
            spinnerClassName={classes.spinner}
            spinner={{
              name: "downloadGuestProfilesGridData",
              size: 20
            }}
          />
        )}
      </div>
      <GuestGrid
        guestProfiles={guestProfiles}
        gridOptions={gridOptions}
        setGridOptions={setGridOptions}
        deleteGuestProfile={handleDeleteGuestProfile}
        reloadGrid={() => getGuestProfilesAction(gridOptions)}
      />
    </div>
  );
};

export default GuestTab;
